import React from 'react';
import { motion } from 'framer-motion';
import { pagePaths } from '../utils/sitePaths';

const footerLinks = [
  { label: 'Home', page: 'home' },
  { label: 'Catering', page: 'menu' },
  { label: 'Decor', page: 'decor' },
  { label: 'Spit Braai', page: 'spitbraai' },
  { label: 'Sound Hire', page: 'sound' },
  { label: 'Team Building', page: 'teambuilding' },
  { label: 'Venue Support', page: 'venuesupport' },
  { label: 'Estimate & Book', page: 'estimate' },
  { label: 'Blog', page: 'blog' },
  { label: 'Contact', page: 'contact' },
];

const Footer = () => {
  const navigateTo = (page, event) => {
    if (event) event.preventDefault();
    window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
  };

  return (
    <footer className="bg-gray-800 text-white py-10 md:py-12">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
        >
          {/* Company Info */}
          <div className="p-5 rounded-2xl border border-white/10 bg-gray-900/20 md:p-0 md:border-0 md:bg-transparent">
            <a
              href={pagePaths.home}
              onClick={(e) => navigateTo('home', e)}
              className="block text-2xl font-playfair font-bold text-gold mb-4 hover:opacity-90"
            >
              UBUNTU EVENTS MANAGEMENT
            </a>
            <p className="text-gray-300 mb-4">
              One Partner for Memorable Events
            </p>
            <p className="text-gray-400 text-sm">
              Ubuntu Enterprises is a full-service event solutions company handling catering, decor, sound, equipment hire, team building, and venue support.
            </p>

            <nav aria-label="Footer navigation" className="hidden md:block mt-6">
              <div className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-3">
                Site Links
              </div>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {footerLinks.map((link) => (
                  <li key={link.page}>
                    <a
                      href={pagePaths[link.page]}
                      onClick={(e) => navigateTo(link.page, e)}
                      className="text-gray-300 hover:text-gold transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Mobile quick links */}
            <div className="mt-5 md:hidden">
              <div className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-3">
                Quick Links
              </div>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={pagePaths.home}
                  onClick={(e) => navigateTo('home', e)}
                  className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left text-sm block"
                >
                  🏠 Home
                </a>
                <a
                  href={pagePaths.menu}
                  onClick={(e) => navigateTo('menu', e)}
                  className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left text-sm block"
                >
                  🍽 Catering
                </a>
                <a
                  href={pagePaths.decor}
                  onClick={(e) => navigateTo('decor', e)}
                  className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left text-sm block"
                >
                  🎨 Decor
                </a>
                <a
                  href={pagePaths.spitbraai}
                  onClick={(e) => navigateTo('spitbraai', e)}
                  className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left text-sm block"
                >
                  🔥 Spit Braai
                </a>
                <a
                  href={pagePaths.sound}
                  onClick={(e) => navigateTo('sound', e)}
                  className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left text-sm block"
                >
                  🎧 Sound Hire
                </a>
                <a
                  href={pagePaths.estimate}
                  onClick={(e) => navigateTo('estimate', e)}
                  className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left text-sm block"
                >
                  💰 Estimate
                </a>
                <a
                  href={pagePaths.blog}
                  onClick={(e) => navigateTo('blog', e)}
                  className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-left text-sm block"
                >
                  📝 Blog
                </a>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="p-5 rounded-2xl border border-white/10 bg-gray-900/20 md:p-0 md:border-0 md:bg-transparent">
            <h4 className="text-lg font-semibold text-gold mb-4">Contact Us</h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-3">
                <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-300">Pretoria, South Africa</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                <span className="text-gray-300">066 328 6231</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <span className="text-gray-300">info@ubuntueventsmanagement.co.za</span>
              </div>
            </div>
          </div>

          {/* Social Media & Hours */}
          <div className="p-5 rounded-2xl border border-white/10 bg-gray-900/20 md:p-0 md:border-0 md:bg-transparent">
            <h4 className="text-lg font-semibold text-gold mb-4">Follow Us</h4>
            <div className="flex space-x-4 mb-6">
              <a
                href="https://www.facebook.com/profile.php?id=61578477748197"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors duration-300 transform hover:scale-110"
                aria-label="Follow us on Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/ubuntu_events_management?igsh=Zzc4NHN2ZW9vOGFz"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-full transition-all duration-300 transform hover:scale-110"
                aria-label="Follow us on Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://tiktok.me/streak/ZSH7EbspTDTbh-mdBYP/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 bg-black hover:bg-gray-800 text-white rounded-full transition-colors duration-300 transform hover:scale-110"
                aria-label="Follow us on TikTok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
              </a>
              <a
                href="https://whatsapp.com/channel/0029VbCQSuA6mYPVb6eqDz0z"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors duration-300 transform hover:scale-110"
                aria-label="Follow the UBUNTU EVENTS MANAGEMENT WhatsApp channel"
                title="WhatsApp channel"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
              </a>
            </div>
            <div className="text-sm text-gray-400">
              <p className="mb-1">Mon-Fri: 8AM-6PM</p>
              <p>Saturday: 9AM-4PM</p>
            </div>
          </div>
        </motion.div>
        
        <div className="border-t border-gray-700 pt-6 mt-8">
          <div className="text-center">
            <p className="text-gray-400 text-sm">
              © 2026 UBUNTU EVENTS MANAGEMENT. All Rights Reserved.
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Designed by TEN15
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;